import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { IAgreementInstallment, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";

interface Props {
	open: boolean;
	agreementId: string;
	installments: IAgreementInstallment[];
	onClose: () => void;
	onConfirm: () => void;
}

const isEligible = (installment: IAgreementInstallment) => installment.status !== "paid" && installment.status !== "canceled";

export const ModalBulkCancelInstallments = ({ open, agreementId, installments, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [reason, setReason] = useState("");
	const [reasonError, setReasonError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const eligible = installments.filter(isEligible);
	const skippedCount = installments.length - eligible.length;

	useEffect(() => {
		if (open) {
			setReason("");
			setReasonError(false);
		}
	}, [open]);

	const handleConfirm = async () => {
		if (!reason.trim()) {
			setReasonError(true);
			return;
		}

		setIsSubmitting(true);
		const results = await Promise.all(
			eligible.map((installment) => SinisterAgreementService.cancelInstallment(agreementId, installment.number, { reason: reason.trim() }))
		);
		setIsSubmitting(false);

		const failures = results.filter((result) => result instanceof Error) as Error[];
		const successCount = results.length - failures.length;

		if (successCount > 0) {
			toastHandler.present({
				type: failures.length > 0 ? "warning" : "success",
				messageOrContent:
					failures.length > 0 ? `${successCount} parcela(s) cancelada(s). ${failures.length} falharam.` : `${successCount} parcela(s) cancelada(s).`,
				position: "top-right",
			});
		} else if (failures.length > 0) {
			toastHandler.present({ type: "error", messageOrContent: failures[0].message, position: "top-right" });
		}

		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Cancelar Parcelas em Massa
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="error" sx={{ py: 0.5 }}>
							{eligible.length} parcela(s) selecionada(s) — total{" "}
							{formatScaleValueBR(String(eligible.reduce((sum, installment) => sum + installment.value, 0)))}. Essa ação cancela o
							boleto/cobrança de todas elas.
						</Alert>

						{skippedCount > 0 && (
							<Alert severity="info" sx={{ py: 0.5 }}>
								{skippedCount} parcela(s) selecionada(s) já paga(s) ou cancelada(s) serão ignoradas.
							</Alert>
						)}

						<TextField
							fullWidth
							multiline
							minRows={3}
							label="Motivo do cancelamento"
							value={reason}
							onChange={(e) => {
								setReason(e.target.value);
								if (e.target.value.trim()) setReasonError(false);
							}}
							error={reasonError}
							helperText={reasonError ? "Informe o motivo do cancelamento." : "Aplicado a todas as parcelas selecionadas."}
							required
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Box flex={1} />
					<Button variant="contained" color="error" disabled={isSubmitting || eligible.length === 0} onClick={handleConfirm}>
						Confirmar Cancelamento ({eligible.length})
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
